/* eslint-disable guard-for-in */
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  Inject,
  Injectable,
  Optional,
  PLATFORM_ID,
  Renderer2,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { REQUEST } from '@nguniversal/express-engine/tokens';

import { APP_CONFIG, IAppConfig } from '@app/core/services/app.config';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  previousData: IOgTags;

  constructor(
    @Inject(APP_CONFIG) private readonly appConfig: IAppConfig,
    @Inject(DOCUMENT) private _document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: any,
    @Optional() @Inject(REQUEST) private readonly request: any,
    private readonly titleService: Title,
    private readonly metaService: Meta,
  ) {}

  updateMetaTags(data: IOgTags, indexable = true): void {
    this.titleService.setTitle(data.title);
    this.updateIndexing(indexable);

    let image = data.image;
    if (image != null) {
      image = image
        ?.replace('original', 'thumbnail_1800x600')
        ?.replace('.png', '.jpg');
    }
    if (image == null) {
      image = this.appConfig.configsUrl + this.appConfig.settings.logo;
    }

    const canonicalUrl = this.resolveCanonicalUrl(data.url);
    if (canonicalUrl) {
      this.updateCanonicalUrl(canonicalUrl);
    }

    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.metaService.updateTag({
      property: 'og:site_name',
      content:
        this.appConfig.settings.page_title || this.appConfig.settings.title,
    });
    this.metaService.updateTag({
      name: 'twitter:title',
      content: data.title,
    });
    this.metaService.updateTag({
      name: 'twitter:description',
      content: data.description,
    });
    this.metaService.updateTag({
      name: 'twitter:image',
      content: image,
    });
    this.metaService.updateTag({
      name: 'twitter:url',
      content: canonicalUrl,
    });
    this.metaService.updateTag({
      name: 'description',
      content: data.description,
    });

    const openGraphData = {
      ...data,
      image,
      url: canonicalUrl,
    };

    for (const key in openGraphData) {
      if (openGraphData[key] != null) {
        this.metaService.updateTag({
          property: `og:${key}`,
          content: openGraphData[key],
        });
      }
    }
  }

  buildPublicUrl(path: string): string {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const publicOrigin = this.getPublicOrigin();

    return `${publicOrigin}${normalizedPath}`;
  }

  private getPublicOrigin(): string {
    const currentOrigin = this.getCurrentOrigin();
    if (currentOrigin && !this.isLocalOrigin(currentOrigin)) {
      return currentOrigin;
    }

    const configName = this.appConfig.settings.config_name;
    if (environment.production) {
      return configName && configName !== 'core'
        ? `https://${configName}.brightrtravel.com`
        : 'https://brightrtravel.com';
    }

    const subdomainPrefix = configName
      ? `${configName}${environment.stage ? '-stage' : '-dev'}-`
      : '';

    return `https://${subdomainPrefix}beacon-me-dev.fezr.com`;
  }

  private getCurrentOrigin(): string | null {
    const requestHost =
      this.getRequestHeader('x-forwarded-host') || this.getRequestHeader('host');
    if (requestHost) {
      const protocol =
        this.getRequestHeader('x-forwarded-proto') ||
        this.request?.protocol ||
        'https';

      return `${protocol}://${requestHost}`.replace(/\/+$/, '');
    }

    const location = this._document?.location;
    if (!location?.origin || location.origin === 'null') {
      return null;
    }

    return location.origin.replace(/\/+$/, '');
  }

  private getRequestHeader(name: string): string | null {
    const value = this.request?.get?.(name) || this.request?.headers?.[name];
    const headerValue = Array.isArray(value) ? value[0] : value;

    return headerValue?.split(',')[0]?.trim() || null;
  }

  private isLocalOrigin(origin: string): boolean {
    const hostname = origin.replace(/^https?:\/\//i, '').split('/')[0].split(':')[0];

    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname.endsWith('.localhost')
    );
  }

  setJsonLd(renderer2: Renderer2, data: any): void {
    const normalizedData = { ...data };

    if (normalizedData.image) {
      normalizedData.image = normalizedData.image
        ?.replace('original', 'thumbnail_1800x600')
        ?.replace('.png', '.jpg');
    }
    if (normalizedData.url) {
      normalizedData.url = this.resolveCanonicalUrl(normalizedData.url);
    }
    if (isPlatformBrowser(this.platformId)) {
      const script = this._document.querySelector('#current-seo-script');
      if (!!script) {
        script.textContent = JSON.stringify(normalizedData);
        return;
      }
    }
    const script = renderer2.createElement('script');
    script.type = `application/ld+json`;
    script.id = `current-seo-script`;
    script.text = JSON.stringify(normalizedData);

    renderer2.appendChild(this._document.body, script);
  }

  setIndexOff(): void {
    this.updateIndexing(false);
  }

  private resolveCanonicalUrl(url?: string): string | null {
    const candidate = url || this._document?.location?.href;
    if (!candidate) {
      return null;
    }

    const publicOrigin = this.getPublicOrigin();
    const resolvedUrl = new URL(candidate, `${publicOrigin}/`);

    // Absolute URLs and the SSR document may still contain the proxy's internal host.
    return `${publicOrigin}${resolvedUrl.pathname}`;
  }

  private updateCanonicalUrl(url: string): void {
    let link = this._document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );

    if (!link) {
      link = this._document.createElement('link');
      link.setAttribute('rel', 'canonical');
      // Domino, which renders the SSR document, does not implement Element.append().
      // eslint-disable-next-line unicorn/prefer-dom-node-append
      this._document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  private updateIndexing(indexable: boolean): void {
    this.metaService.updateTag({
      name: 'robots',
      content: indexable ? 'index, follow' : 'noindex, nofollow',
    });
  }

  // TODO add method for <meta name="google-site-verification" content="+nxGUDJ4QpAZ5l9Bsjdi102tLVC21AIh5d1Nl23908vVuFHs34="/>
}

export interface IOgTags {
  // [key: string]: string;
  title: string;
  description: string;
  type: 'article' | 'website';
  image: string | null;
  url?: string; // canonical URL
  locale?: string; // 'en_US'
}
